// Story 1.24 — TanStack Query hook for GET /api/v1/portfolios/applications/me.
//
// Backs the collapsible "My applications" section on
// /account/creator-portfolios. Story 1.26's dashboard "Applications"
// tab will consume this same hook once it ships.

import { useQuery } from "@tanstack/react-query";

import { attachRequestIdToScope } from "../../lib/api-client";
import type {
  MyApplicationsStatusFilter as SharedMyApplicationsStatusFilter,
  PortfolioApplicationStatus,
} from "../../lib/portfolio-status-badge";

export type MyPortfolioApplicationStatus = PortfolioApplicationStatus;

export type MyPortfolioApplication = {
  id: string;
  portfolio_id: string;
  portfolio_title: string;
  portfolio_theme: string | null;
  portfolio_recruiting_status: string;
  optional_drop_specific_hello_text: string | null;
  status: MyPortfolioApplicationStatus;
  submitted_at: string;
  reviewed_at: string | null;
  created_at: string;
};

export type MyPortfolioApplicationsResponse = {
  data: MyPortfolioApplication[];
  page: number;
  page_size: number;
  has_next: boolean;
};

// Re-exported from the shared module so existing call-sites
// (CreatorPortfoliosPage) keep working without changing imports.
// Story 1.26 onwards: new consumers should import directly from
// `lib/portfolio-status-badge`.
export type MyApplicationsStatusFilter = SharedMyApplicationsStatusFilter;

async function fetchMyApplications(
  statusFilter: MyApplicationsStatusFilter,
  page: number,
): Promise<MyPortfolioApplicationsResponse> {
  const params = new URLSearchParams({
    status: statusFilter,
    page: String(page),
    page_size: "50",
  });
  const response = await fetch(
    `/api/v1/portfolios/applications/me?${params.toString()}`,
    { credentials: "include" },
  );
  attachRequestIdToScope(response);
  if (!response.ok) {
    throw new Error(
      `My applications fetch failed (HTTP ${response.status})`,
    );
  }
  return (await response.json()) as MyPortfolioApplicationsResponse;
}

export function useMyPortfolioApplications(
  statusFilter: MyApplicationsStatusFilter = "any",
  page = 1,
) {
  return useQuery({
    queryKey: ["creator-portfolios", "my-applications", statusFilter, page],
    queryFn: () => fetchMyApplications(statusFilter, page),
    staleTime: 30_000,
    refetchOnWindowFocus: true,
  });
}
